import React, { useState } from "react";
import { Link } from "wouter";
import { Property } from "../../types/schema";
import { MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import WishlistButton from "../ui/WishlistButton";
import ShareButton from "../ui/ShareButton";
import { Button } from "../ui/button";
import StatusBadge from "../ui/StatusBadge";
import ResponsiveImage from "../ui/ResponsiveImage";

interface PropertyCardProps {
  property: Property;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const navigateToImage = (index: number) => {
    if (property.images && index >= 0 && index < property.images.length) {
      setCurrentImageIndex(index);
    }
  };

  const navigatePrevious = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (property.images && property.images.length > 0) {
      const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : property.images.length - 1;
      navigateToImage(newIndex);
    }
  };

  const navigateNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (property.images && property.images.length > 0) {
      const newIndex = currentImageIndex < property.images.length - 1 ? currentImageIndex + 1 : 0;
      navigateToImage(newIndex);
    }
  };

  const currentImage = property.images?.[currentImageIndex];
  const apiUrl = import.meta.env.VITE_STRAPI_URL || 'http://localhost:1337';
  const imageUrl = currentImage ?
    `${apiUrl}${currentImage.attributes?.url || ''}` :
    null;

  return (
    <div className="relative rounded-lg overflow-hidden shadow-lg group w-full h-[500px] bg-white">
      <div className="absolute inset-0 overflow-hidden">
        {imageUrl ? (
          <ResponsiveImage
            src={imageUrl}
            alt={`${property.Title} - Immobilie in ${property.city}`}
            aspectRatio="4/3"
            className="w-full h-full group-hover:scale-105 transition-transform duration-500"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            objectFit="cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
            Kein Bild verfügbar
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
      </div>

      {/* Image Navigation Arrows */}
      {property.images && property.images.length > 1 && (
        <>
          <button
            onClick={navigatePrevious}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-30"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={navigateNext}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-30"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Status Badge */}
      <div className="absolute top-4 left-4 z-20">
        <StatusBadge status={property.status} />
      </div>

      {/* Content overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
        {/* Location */}
        <div className="flex items-center mb-2">
          <MapPin className="h-4 w-4 mr-1" />
          <span className="text-sm">
            {property.district ? `${property.district}, ` : ''}{property.city}
          </span>
        </div>

        {/* Property Title */}
        <h3 className="text-2xl font-bold mb-2">{property.Title}</h3>

        {/* Property Details */}
        <div className="flex items-center space-x-4 mb-4 text-sm">
          <span className="font-bold text-lg">{property.price.toLocaleString('de-DE')} {property.priceUnit}</span>
          <span>{property.area} m²</span>
          <span>{property.rooms} Zi.</span>
        </div>

        {/* "Details anzeigen" Button */}
        <Link href={`/immobilie/${property.documentId}`}>
          <Button
            variant="outline"
            className="bg-white/10 border-white text-white hover:bg-white hover:text-black transition-all duration-300"
          >
            Details anzeigen
          </Button>
        </Link>
      </div>

      {/* Wishlist and Share buttons */}
      <div className="absolute top-4 right-4 flex space-x-2 z-20">
        <WishlistButton propertyId={property.id} />
        <ShareButton property={property} />
      </div>
    </div>
  );
};

export default PropertyCard;
